import React, { useMemo, useCallback, useRef, useEffect } from "react";
import { apiDomain } from "configs/configs";
import { logout } from "helpers/helpers";
import {
  Input,
  Form,
  Row,
  Typography,
  Button,
  notification,
  Space,
} from "antd";
import Table, { ColumnsType, TableSummary } from "components/table/table";
import MobileInnerWrapper from "layouts/mobileInnerWrapper/mobileInnerWrapper";
import useScheduleStore from "stores/master/schedule";
import useUserStore from "stores/user";
import useSettingsStore, { SettingsElementType } from "stores/settings";
import {
  MasterObjectType,
  MasterPayoutObjectType,
} from "pages/master/schedule/types";
import styles from "pages/master/schedule/schedule.module.scss";

const { Text, Title } = Typography;

type ComponentProps = {};

const Page: React.FC<ComponentProps> = () => {
  const [form] = Form.useForm();

  const {
    daysCount,
    list,
    setList,
    payoutList,
    setPayoutList,
    isPostInProcess,
    setIsPostInProcess,
    fromDate,
    setFromDate,
  } = useScheduleStore((store) => store);

  const { user } = useUserStore((store) => store);
  const { settingsList, setSettingsList } = useSettingsStore((store) => store);
  const listRef = useRef(list);
  const payoutListRef = useRef(payoutList);

  //  Error while saving change status
  const openNotificationWithError = useCallback(
    ({ message }: { message: string }) => {
      notification.error({
        message: "Ошибка",
        description: message,
        placement: "topRight",
        duration: 3,
      });
    },
    []
  );

  //  Success message
  const openNotificationWithSuccess = useCallback(
    ({ message }: { message: string }) => {
      notification.success({
        message: "Сохранено",
        description: message,
        placement: "topRight",
        duration: 3,
      });
    },
    []
  );

  const handleEditObjectSchedule = useCallback(
    ({
      objectId,
      date,
      value,
    }: {
      objectId: number;
      date: string;
      value: string;
    }) => {
      const userId = user.userData.id;

      if (!userId) return;

      setList((list) => {
        const listData = list.data.map((dataItem) => {
          if (dataItem.id !== objectId) return dataItem;

          const schedule = dataItem.schedule;

          const foundScheduleItem = schedule.find((scheduleItem) => {
            if (scheduleItem.object_id !== objectId) return false;
            if (scheduleItem.date !== date) return false;

            return true;
          });

          const valueCleaned = !value
            ? ""
            : value
                .replace(/[^\d.,]/g, "")
                .replace(/,/g, ".")
                .replace(/^(\d*(\.\d{0,2})?).*$/, "$1");

          if (foundScheduleItem) foundScheduleItem.value = valueCleaned;

          if (!foundScheduleItem)
            schedule.push({
              object_id: objectId,
              master_id: userId,
              date: date,
              value: valueCleaned,
              isNotSaved: true,
              confirmed_by_senior_master: false,
              confirmed_by_senior_master_id: null,
              calculated_sum: "0",
            });

          return { ...dataItem, schedule: schedule };
        });

        return {
          ...list,
          data: listData,
        };
      });
    },
    [user, setList]
  );

  const lockPeriodDays = useMemo(() => {
    if (
      !settingsList ||
      !settingsList.isLoaded ||
      !Array.isArray(settingsList.data)
    )
      return 0;

    const setting = settingsList.data.find(
      (item) => item.key === "lock_period_days"
    );

    if (!setting || setting.value === undefined || setting.value === null)
      return 0;

    const parsedValue = parseInt(setting.value, 10);

    if (isNaN(parsedValue)) return 0;

    return parsedValue;
  }, [settingsList]);

  const scheduleData = useMemo(() => {
    if (!list.isLoaded) return [];

    return list.data.filter((object) => {
      if (object.status !== "in_progress") return false;

      const master = object.masters
        ? object.masters.find((master) => master.id === user.userData.id)
        : undefined;

      if (!master) return false;

      if (master.status === "unactive") return false;

      return true;
    });
  }, [list, user]);

  const payoutScheduleData = useMemo(() => {
    if (!payoutList.isLoaded && !payoutList.data?.length) return [];

    return payoutList.data;
  }, [payoutList]);

  const daysOfWeek = useMemo(() => {
    return ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"];
  }, []);

  const scheduleColumns = useMemo(() => {
    const result: ColumnsType = [
      {
        title: "Объект",
        key: "id",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterObjectType;

          return <div>{record.address ? record.address : record.name}</div>;
        },
      },
    ];

    const currentDate = new Date();
    let previousMonth = currentDate.getMonth();
    let children: ColumnsType = [];

    for (let daysToAdd = 0; daysToAdd <= daysCount - 1; daysToAdd++) {
      const futureDate = new Date(currentDate);
      futureDate.setDate(currentDate.getDate() + daysToAdd);

      const dateString = futureDate.toISOString().split("T")[0];
      const day = futureDate.getDate();
      const dayOfWeek = daysOfWeek[futureDate.getDay()];

      // Проверка на смену месяца до вывода даты
      const checkDate = new Date(futureDate);
      checkDate.setDate(futureDate.getDate() + 1);

      // Записываем дату до смены месяца
      children.push({
        title: (
          <>
            {dayOfWeek}
            <br />
            {day}
          </>
        ),
        key: dateString,
        align: "center",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterObjectType;

          const scheduleItem = record.schedule.find((scheduleItem) => {
            if (scheduleItem.object_id !== record.id) return false;
            if (scheduleItem.date !== dateString) return false;

            return true;
          });

          const value = scheduleItem ? scheduleItem.value : "";

          if (
            (lockPeriodDays > daysToAdd &&
              !!scheduleItem &&
              !scheduleItem?.isNotSaved) ||
            scheduleItem?.confirmed_by_senior_master
          )
            return (
              <Text
                strong={!!scheduleItem?.confirmed_by_senior_master}
                className={styles.noWrap}
              >
                {value ? value : 0}
              </Text>
            );

          return (
            <Input
              size="small"
              name={`objects[${record.id}][${dateString}]`}
              onChange={(event) =>
                handleEditObjectSchedule({
                  objectId: record.id,
                  date: dateString,
                  value: event.target.value,
                })
              }
              value={value}
              disabled={isPostInProcess || !settingsList.isLoaded}
            />
          );
        },
      });

      // Проверка на смену месяца
      if (
        checkDate.getMonth() !== previousMonth ||
        daysToAdd === daysCount - 1
      ) {
        result.push({
          title: `${futureDate.toLocaleString("default", {
            month: "long",
            year: "numeric",
          })}`,
          key: "id",
          children,
        });

        children = [];
        previousMonth = checkDate.getMonth();
      }
    }

    return result;
  }, [
    daysCount,
    daysOfWeek,
    isPostInProcess,
    settingsList,
    lockPeriodDays,
    handleEditObjectSchedule,
  ]);

  //  Save current list to the ref so we can use it in useEffect without trigger it
  useEffect(() => {
    listRef.current = list;
  }, [list]);

  //  Save current list to the ref so we can use it in useEffect without trigger it
  useEffect(() => {
    payoutListRef.current = payoutList;
  }, [payoutList]);

  //  Get all settings
  const getSettings = useCallback(
    async (isRefresh?: boolean) => {
      const fetchData = async () => {
        try {
          const url = `${apiDomain}/api/records/settings`;
          const options: RequestInit = {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
          };

          const response = await fetch(url, options);

          if (!response.ok) throw new Error();

          const data = await response.json();

          if (!data?.records) throw new Error();

          const records: SettingsElementType[] = data.records;

          setSettingsList({ isLoaded: true, data: records });
        } catch (error) {
          logout();
        }
      };

      if (listRef.current.isLoaded && !isRefresh) return;

      await fetchData();
    },
    [setSettingsList]
  );

  //  Get all objects
  const getList = useCallback(
    async (isRefresh?: boolean) => {
      const fetchData = async () => {
        try {
          const url = `${apiDomain}/api/records/objects`;
          const options: RequestInit = {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
          };

          const response = await fetch(url, options);

          if (!response.ok) throw new Error();

          const data = await response.json();

          if (!data?.records) throw new Error();

          const records: MasterObjectType[] = data.records;

          setList({ isLoaded: true, data: records });
        } catch (error) {
          logout();
        }
      };

      if (listRef.current.isLoaded && !isRefresh) return;

      await fetchData();
    },
    [setList]
  );

  //  Get all objects
  const getPayoutList = useCallback(
    async (
      isRefresh?: boolean,
      fromDate: string = new Date().toISOString().split("T")[0]
    ) => {
      const fetchData = async () => {
        try {
          setPayoutList((payoutList) => {
            return { isLoaded: false, data: payoutList.data };
          });

          const url = `${apiDomain}/api/records/objects?daysCount=${daysCount}&fromDate=${fromDate}`;
          const options: RequestInit = {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
          };

          const response = await fetch(url, options);

          if (!response.ok) throw new Error();

          const data = await response.json();

          if (!data?.records) throw new Error();

          const records: MasterPayoutObjectType[] = data.records;

          setPayoutList({ isLoaded: true, data: records });
        } catch (error) {
          logout();
        }
      };

      if (payoutListRef.current.isLoaded && !isRefresh) return;

      await fetchData();
    },
    [setPayoutList, daysCount]
  );

  //  When started, get data
  useEffect(() => {
    getList();
    getSettings();
    getPayoutList();

    return () => {
      //  When leave page reset all
      setList({ isLoaded: false, data: [] });
      setPayoutList({ isLoaded: false, data: [] });
    };
  }, [getList, getSettings, getPayoutList, setList, setPayoutList]);

  //  Submit form, depends of mode
  const handleFinish = useCallback(async () => {
    if (isPostInProcess) return;
    if (!scheduleData?.length) return;

    const fetchData = async () => {
      setIsPostInProcess(true);

      for (const listItem of scheduleData) {
        const url = `${apiDomain}/api/records/objects/${listItem.id}`;

        const options: RequestInit = {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            schedule: listItem.schedule,
          }),
          credentials: "include",
        };

        try {
          const response = await fetch(url, options);

          if (!response.ok) throw new Error();

          openNotificationWithSuccess({
            message: `Расписание объекта ${
              listItem.address ? listItem.address : listItem.name
            } успешно сохранено`,
          });
        } catch (error) {
          openNotificationWithError({
            message: `Не удалось сохранить расписание объекта ${
              listItem.address ? listItem.address : listItem.name
            }`,
          });
        }
      }

      await getList(true);

      if (fromDate === new Date().toISOString().split("T")[0])
        await getPayoutList(true);

      setIsPostInProcess(false);
    };

    fetchData();
  }, [
    isPostInProcess,
    fromDate,
    scheduleData,
    getList,
    getPayoutList,
    openNotificationWithError,
    openNotificationWithSuccess,
    setIsPostInProcess,
  ]);

  const payoutScheduleColumns = useMemo(() => {
    const result: ColumnsType = [
      {
        title: "Объект",
        key: "id",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterPayoutObjectType;

          return <Text>{record.address ? record.address : record.name}</Text>;
        },
      },
      {
        title: "Входящий остаток",
        key: "id",
        align: "right",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterPayoutObjectType;

          const initialBalance = record?.initial_balance
            ? parseFloat(record.initial_balance)
            : 0;

          return (
            <Text strong className={styles.noWrap}>
              {initialBalance.toFixed(2)}
            </Text>
          );
        },
      },
    ];

    const currentDate = new Date(fromDate);
    let previousMonth = currentDate.getMonth();
    let children: ColumnsType = [];

    for (let daysToAdd = 0; daysToAdd <= daysCount - 1; daysToAdd++) {
      const futureDate = new Date(currentDate);
      futureDate.setDate(currentDate.getDate() + daysToAdd);

      const dateString = futureDate.toISOString().split("T")[0];
      const day = futureDate.getDate();
      const dayOfWeek = daysOfWeek[futureDate.getDay()];

      // Проверка на смену месяца до вывода даты
      const checkDate = new Date(futureDate);
      checkDate.setDate(futureDate.getDate() + 1);

      // Записываем дату до смены месяца
      children.push({
        title: (
          <>
            {dayOfWeek}
            <br />
            {day}
          </>
        ),
        key: dateString,
        align: "right",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterPayoutObjectType;

          const scheduleItem = record.schedule.find((scheduleItem) => {
            if (scheduleItem.object_id !== record.id) return false;
            if (scheduleItem.date !== dateString) return false;

            return true;
          });

          const value = scheduleItem?.calculated_sum
            ? parseFloat(scheduleItem.calculated_sum)
            : 0;

          return (
            <Text
              strong={!!scheduleItem?.confirmed_by_senior_master}
              className={styles.noWrap}
            >
              {value.toFixed(2)}
            </Text>
          );
        },
      });

      // Проверка на смену месяца
      if (
        checkDate.getMonth() !== previousMonth ||
        daysToAdd === daysCount - 1
      ) {
        result.push({
          title: `${futureDate.toLocaleString("default", {
            month: "long",
            year: "numeric",
          })}`,
          key: "id",
          children,
        });

        children = [];
        previousMonth = checkDate.getMonth();
      }
    }

    result.push({
      title: "Исходящий остаток",
      key: "id",
      align: "right",
      render: (text: any, untypedRecord: unknown) => {
        const record = untypedRecord as MasterPayoutObjectType;

        const initialBalance = record?.initial_balance
          ? parseFloat(record.initial_balance)
          : 0;

        const outgoingBalance =
          initialBalance +
          record.schedule.reduce((result, scheduleItem) => {
            return (
              result +
              (scheduleItem.confirmed_by_senior_master &&
              scheduleItem.calculated_sum
                ? parseFloat(scheduleItem.calculated_sum)
                : 0)
            );
          }, 0);

        return (
          <Text strong className={styles.noWrap}>
            {outgoingBalance.toFixed(2)}
          </Text>
        );
      },
    });

    return result;
  }, [daysCount, fromDate, daysOfWeek]);

  const handleShowPreviousDates = useCallback(() => {
    setFromDate((fromDate) => {
      const currentFromDate = new Date(fromDate);
      currentFromDate.setDate(currentFromDate.getDate() - daysCount);

      const previousStartDate = currentFromDate.toISOString().split("T")[0];

      getPayoutList(true, previousStartDate);

      return previousStartDate;
    });
  }, [getPayoutList, setFromDate, daysCount]);

  const handleShowNextDates = useCallback(() => {
    setFromDate((fromDate) => {
      const currentFromDate = new Date(fromDate);
      currentFromDate.setDate(currentFromDate.getDate() + daysCount);

      const nextStartDate = currentFromDate.toISOString().split("T")[0];

      getPayoutList(true, nextStartDate);

      return nextStartDate;
    });
  }, [getPayoutList, setFromDate, daysCount]);

  return (
    <MobileInnerWrapper>
      <Title level={3} className={styles.title}>
        Редактирование графика
      </Title>
      <div className={styles.contentBlock}>
        <Title level={4} className={styles.subTitle}>
          График работы
        </Title>
        {!!(!!list.isLoaded && !scheduleData?.length) && (
          <Text>
            На данный момент у вас нет активных объектов, где вы назначены
            мастером.
          </Text>
        )}
        {!!(!list.isLoaded || !!scheduleData?.length) && (
          <Form layout={"vertical"} form={form} onFinish={handleFinish}>
            <Table
              size="small"
              dataSource={scheduleData}
              columns={scheduleColumns}
              rowKey="id"
              pagination={false}
              loading={!list.isLoaded || !settingsList.isLoaded}
              styles={styles}
              className={styles.table}
            />

            <Form.Item>
              <Button
                type="primary"
                size="large"
                className={styles.button}
                disabled={!list.isLoaded || !settingsList.isLoaded}
                htmlType="submit"
                loading={isPostInProcess}
              >
                Сохранить
              </Button>
            </Form.Item>
          </Form>
        )}
      </div>
      <div className={styles.contentBlock}>
        <Row justify="space-between" align="middle" className={styles.subTitle}>
          <Title level={4}>График выплат</Title>
          <Space>
            <Button
              onClick={handleShowPreviousDates}
              disabled={!payoutList.isLoaded}
            >
              Показать предыдущие даты
            </Button>
            <Button
              onClick={handleShowNextDates}
              disabled={!payoutList.isLoaded}
            >
              Показать следующие даты
            </Button>
          </Space>
        </Row>
        <Table
          size="small"
          dataSource={payoutScheduleData}
          columns={payoutScheduleColumns}
          rowKey="id"
          pagination={false}
          loading={!payoutList.isLoaded}
          styles={styles}
          className={styles.table}
          summary={(rawPageData) => {
            const pageData = rawPageData as MasterPayoutObjectType[];

            let totalInitialBalance = 0;

            pageData.forEach((object) => {
              totalInitialBalance += object?.initial_balance
                ? parseFloat(object.initial_balance)
                : 0;
            });

            let totalCurrentDayBalance = totalInitialBalance;

            return (
              <TableSummary>
                <TableSummary.Row>
                  <TableSummary.Cell index={0}>
                    <Text strong>Итого</Text>
                  </TableSummary.Cell>
                  <TableSummary.Cell index={1} align="right">
                    <Text strong className={styles.noWrap}>
                      {totalInitialBalance.toFixed(2)}
                    </Text>
                  </TableSummary.Cell>
                  {Array.from({ length: daysCount }).map((_, daysToAdd) => {
                    const dateToCheck = new Date(fromDate);
                    dateToCheck.setDate(dateToCheck.getDate() + daysToAdd);

                    const dateString = dateToCheck.toISOString().split("T")[0];

                    // Суммируем значения value для всех записей pageData на текущую дату
                    const totalValue = pageData.reduce((result, object) => {
                      const scheduleItem = object.schedule.find(
                        (scheduleItem) => {
                          return (
                            scheduleItem.object_id === object.id &&
                            scheduleItem.date === dateString
                          );
                        }
                      );

                      return (
                        result +
                        (scheduleItem?.confirmed_by_senior_master &&
                        scheduleItem?.calculated_sum
                          ? parseFloat(scheduleItem.calculated_sum)
                          : 0)
                      );
                    }, 0);

                    totalCurrentDayBalance += totalValue;

                    // Рендерим одну ячейку с итоговой суммой
                    return (
                      <TableSummary.Cell
                        key={`day-${daysToAdd}`}
                        index={daysToAdd + 2}
                        align="right"
                      >
                        <Text strong className={styles.noWrap}>
                          {totalCurrentDayBalance.toFixed(2)}
                        </Text>
                      </TableSummary.Cell>
                    );
                  })}
                  <TableSummary.Cell index={daysCount + 1} align="right">
                    <Text strong className={styles.noWrap}>
                      {totalCurrentDayBalance.toFixed(2)}
                    </Text>
                  </TableSummary.Cell>
                </TableSummary.Row>
              </TableSummary>
            );
          }}
        />
      </div>
    </MobileInnerWrapper>
  );
};

export default Page;
